import { Calendar, Clock, MapPin, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import useScrollReveal from "@/hooks/useScrollReveal";
import { apiGet } from "@/lib/api";

// Kochkurs-Termine 2026 – Thermomix Studio Kempen, dynamisch aus dem Backend

const MONTHS_SHORT = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

const KochkursTermineSection = () => {
  const [termine, setTermine] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const sectionRef = useScrollReveal(termine.length);

  useEffect(() => {
    apiGet("/content/kochkurse")
      .then((items) => setTermine(items))
      .catch(() => setTermine([]))
      .finally(() => setLoaded(true));
  }, []);

  const today = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })();
  const parseLocalDate = (iso) => { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d); };

  const isPast = (dateStr) => parseLocalDate(dateStr) < today;
  const upcomingCount = termine.filter((t) => !isPast(t.date)).length;

  const buildWhatsAppLink = (termin) => {
    const d = (() => { const [y, m, dd] = termin.date.split("-").map(Number); return new Date(y, m - 1, dd); })();
    const dateFormatted = `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
    return `https://wa.me/491721697433?text=${encodeURIComponent(`Hallo Jennifer! Ich möchte mich für den Kochkurs "${termin.title}" am ${dateFormatted} anmelden.`)}`;
  };

  return (
    <section
      id="kochkurs-termine"
      data-testid="kochkurs-termine-section"
      ref={sectionRef}
      className="section-padding bg-gradient-to-b from-stone-50 via-white to-stone-50 relative overflow-hidden"
    >
      <div className="absolute top-1/4 -right-40 w-[500px] h-[500px] bg-emerald-200/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -left-40 w-[500px] h-[500px] bg-amber-200/15 rounded-full blur-3xl pointer-events-none" />

      <div className="container-custom relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-12 reveal">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4">
            <Calendar className="w-3.5 h-3.5" /> Kochkurs-Kalender 2026
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4 font-[Outfit]">
            Kostenlose Kochkurse in Kempen
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed mb-6">
            12 spannende Themenabende – von Fitness-Küche bis Festtagsmenü. Komplett <strong className="text-emerald-600">kostenlos</strong> für Kunden & Interessenten.
          </p>
          <div className="inline-flex flex-wrap items-center justify-center gap-3">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-sm font-semibold">
              <Sparkles className="w-4 h-4" /> {upcomingCount} offene Termine
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-100 text-amber-700 text-sm font-semibold">
              <MapPin className="w-4 h-4" /> Thermomix® Studio Kempen
            </span>
          </div>
        </div>

        {/* Termin-Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto reveal reveal-delay-1 mb-12">
          {termine.map((t) => {
            const past = isPast(t.date);
            const d = parseLocalDate(t.date);
            return (
              <div
                key={t.date}
                data-testid={`termin-card-${t.date}`}
                className={`relative rounded-2xl p-6 border transition-all duration-300 flex flex-col ${
                  past
                    ? "bg-stone-50 border-stone-200 opacity-60"
                    : t.highlight
                    ? "glass-card border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white hover:shadow-xl hover:-translate-y-1"
                    : "glass-card hover:shadow-xl hover:-translate-y-1"
                }`}
              >
                {past && (
                  <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-stone-200 text-stone-500 text-[9px] font-bold uppercase tracking-wider">
                    <CheckCircle2 className="w-3 h-3" /> vergangen
                  </div>
                )}
                {!past && t.highlight && (
                  <div className="absolute -top-2.5 right-4 flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-[9px] font-bold uppercase tracking-wider shadow-md">
                    <Sparkles className="w-2.5 h-2.5" /> Samstag-Special
                  </div>
                )}

                <div className="flex items-start gap-4 mb-4">
                  {/* Datums-Kachel */}
                  <div className={`shrink-0 w-16 rounded-xl overflow-hidden text-center shadow-sm ${past ? "bg-stone-200" : "bg-white ring-1 ring-emerald-100"}`}>
                    <div className={`text-[10px] font-bold uppercase tracking-widest py-1 ${past ? "bg-stone-300 text-stone-500" : "bg-emerald-500 text-white"}`}>
                      {t.day}
                    </div>
                    <div className={`text-2xl font-extrabold font-[Outfit] leading-none pt-2 ${past ? "text-stone-500" : "text-slate-900"}`}>
                      {d.getDate()}
                    </div>
                    <div className={`text-[10px] font-semibold uppercase pb-2 pt-0.5 ${past ? "text-stone-500" : "text-emerald-700"}`}>
                      {MONTHS_SHORT[d.getMonth()]} {String(d.getFullYear()).slice(-2)}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="text-3xl mb-1 leading-none">{t.emoji}</div>
                    <h3 className={`font-bold font-[Outfit] text-lg leading-tight ${past ? "text-slate-500" : "text-slate-900"}`}>
                      {t.title}
                    </h3>
                    <p className={`text-xs mt-1 leading-relaxed ${past ? "text-slate-400" : "text-slate-500"}`}>
                      {t.tag}
                    </p>
                  </div>
                </div>

                <div className={`flex items-center gap-2 text-xs font-semibold mb-4 mt-auto pt-2 ${past ? "text-stone-400" : "text-slate-600"}`}>
                  <Clock className="w-3.5 h-3.5" />
                  <span>{t.time} Uhr</span>
                </div>

                {past ? (
                  <div className="text-[11px] text-stone-400 text-center py-2 border-t border-stone-200">
                    Termin bereits stattgefunden
                  </div>
                ) : (
                  <a
                    href={buildWhatsAppLink(t)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`termin-anmelden-${t.date}`}
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-full bg-slate-900 text-white text-xs font-bold hover:bg-emerald-600 transition-all group"
                  >
                    Per WhatsApp anmelden
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </a>
                )}
              </div>
            );
          })}
        </div>

        {/* Info-Leiste unten */}
        <div className="max-w-4xl mx-auto reveal reveal-delay-2">
          <div className="glass-card rounded-3xl p-6 md:p-8">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-emerald-600 mb-3">
                  <MapPin className="w-4 h-4" /> Veranstaltungsort
                </div>
                <div className="font-bold text-slate-900 font-[Outfit] text-lg mb-1">Thermomix® Studio</div>
                <div className="text-slate-600 text-sm leading-relaxed">
                  Hooghe Weg 10<br />47906 Kempen
                </div>
                <a
                  href="https://maps.google.com/maps?q=Hooghe+Weg+10,+47906+Kempen"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold mt-3 hover:underline"
                >
                  In Google Maps öffnen <ArrowRight className="w-3 h-3" />
                </a>
              </div>
              <div>
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-emerald-600 mb-3">
                  <Calendar className="w-4 h-4" /> Anmeldung
                </div>
                <div className="text-slate-600 text-sm leading-relaxed mb-2">
                  Anmeldung bis <strong className="text-slate-900">12:00 Uhr am Vortag</strong> per WhatsApp oder telefonisch.
                </div>
                <div className="text-slate-500 text-xs leading-relaxed italic">
                  Sollten Sie verhindert sein, bitten wir um eine zeitnahe Absage, damit weitere Gäste der Warteliste nachrücken können. Danke!
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default KochkursTermineSection;
